import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import {
  UseFieldArrayAppend,
  UseFieldArrayRemove,
} from 'react-hook-form';
import {
  VisitNoteData,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent';
import {
  VisitNoteBillingCode,
} from 'views/EMR/components/ServiceLookup/type';
import compact from 'lodash/compact';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';

export type VisitNoteFormBridgeValue = {
  billingCodes: VisitNoteBillingCode[];
  appendBillingCode: UseFieldArrayAppend<VisitNoteData, 'bill.serviceCodes'>;
  removeBillingCode: UseFieldArrayRemove;
  syncFromForm: (
    codes: VisitNoteBillingCode[],
    append: UseFieldArrayAppend<VisitNoteData, 'bill.serviceCodes'>,
    remove: UseFieldArrayRemove,
  ) => void;
  needsRefresh: boolean;
};

const defaultVisitNoteFormBridgeValue: VisitNoteFormBridgeValue = {
  billingCodes: [],
  appendBillingCode: noop as UseFieldArrayAppend<VisitNoteData, 'bill.serviceCodes'>,
  removeBillingCode: noop as UseFieldArrayRemove,
  syncFromForm: noop,
  needsRefresh: false,
};

export const VisitNoteFormBridgeContext = createContext<VisitNoteFormBridgeValue>(
  defaultVisitNoteFormBridgeValue,
);

export function useVisitNoteFormBridge(): VisitNoteFormBridgeValue {
  return useContext(VisitNoteFormBridgeContext);
}

export default function VisitNoteFormBridgeProvider({ children }: PropsWithChildren) {
  const [billingCodes, setBillingCodes] = useState<VisitNoteBillingCode[]>([]);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const appendRef = useRef<UseFieldArrayAppend<VisitNoteData, 'bill.serviceCodes'>>(
    noop as UseFieldArrayAppend<VisitNoteData, 'bill.serviceCodes'>,
  );
  const removeRef = useRef<UseFieldArrayRemove>(
    noop as UseFieldArrayRemove,
  );

  useEffect(
    () => visitNoteEventChannel.on('onVisitNoteConflict', setNeedsRefresh),
    [],
  );

  const appendBillingCode = useCallback<UseFieldArrayAppend<VisitNoteData, 'bill.serviceCodes'>>(
    (value, options) => {
      appendRef.current(value, options);
    },
    [],
  );

  const removeBillingCode = useCallback<UseFieldArrayRemove>(
    (index) => {
      removeRef.current(index);
    },
    [],
  );

  const syncFromForm = useCallback((
    codes: VisitNoteBillingCode[],
    append: UseFieldArrayAppend<VisitNoteData, 'bill.serviceCodes'>,
    remove: UseFieldArrayRemove,
  ) => {
    appendRef.current = append;
    removeRef.current = remove;
    setBillingCodes((previous) => (isEqual(previous, codes) ? previous : compact(codes)));
  }, []);

  const value = useMemo<VisitNoteFormBridgeValue>(
    () => ({
      billingCodes,
      appendBillingCode,
      removeBillingCode,
      syncFromForm,
      needsRefresh,
    }),
    [billingCodes, appendBillingCode, removeBillingCode, syncFromForm, needsRefresh],
  );

  return (
    <VisitNoteFormBridgeContext.Provider value={value}>
      {children}
    </VisitNoteFormBridgeContext.Provider>
  );
}
